import { Link } from "react-router-dom";
import PlayerPassCard from "../components/PlayerPassCard";
import { ArrowRight, Hammer, Trophy, Wallet } from "lucide-react";
import ForumPreview from "../components/ForumPreview";

export default function Landing() {
  return (
    <main className="bg-cream">
      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 relative">
            <div className="font-mono uppercase tracking-widest text-xs mb-4 inline-block bg-ink text-cream px-3 py-1 border-2 border-ink">
              <span className="rec-dot mr-2" />
              The challenge is live · 100+ players
            </div>
            <h1 className="font-display font-extrabold tracking-tight uppercase text-5xl sm:text-7xl lg:text-[5.4rem] leading-[0.95]">
              Fund your business with{" "}
              <span className="highlight-marker">grit</span>,
              <br />
              not <span className="line-through decoration-marker-red decoration-[6px]">privilege</span>.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed">
              <span className="font-display font-extrabold uppercase">#NoJobNoVC</span> is a community of
              entrepreneurs building real businesses without venture capital, a 9-to-5 paycheck,
              or family money. Hackathons. Micro-SaaS. Freelance. Whatever it takes.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register" className="brutal-btn brutal-btn-yellow text-base px-6 py-3" data-testid="cta-join">
                Join the challenge <ArrowRight size={18} />
              </Link>
              <Link to="/login" className="brutal-btn text-base px-6 py-3" data-testid="cta-login">
                Already a player? Log in
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <span className="tag-pill">#MicroSaaS</span>
              <span className="tag-pill" style={{ background: "#FF3333", color: "#fff" }}>#Hackathons</span>
              <span className="tag-pill" style={{ background: "#2B5CFF", color: "#fff" }}>#Freelance</span>
              <span className="tag-pill">#Workshops</span>
              <span className="tag-pill" style={{ background: "#fff" }}>#NotionTemplates</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative flex justify-center">
            <span className="tape top-2 left-8 z-10">official pass ↓</span>
            <PlayerPassCard
              name="Maya Okafor"
              participantId="P-MAYA0042"
              goal={25000}
              joined="2026-01-12"
              rotate="rotate-[3deg]"
            />
            <span className="tape bottom-4 right-2 bg-marker-red text-white border-ink/30 z-10">
              built for sharing
            </span>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="bg-ink text-cream border-y-4 border-ink py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Wallet size={32} />,
                title: "No outside capital",
                body: "No VCs. No angels. No family bailouts. We fund our companies with hackathon prizes, freelance gigs, micro-SaaS, and whatever the day allows.",
                color: "bg-marker-yellow text-ink",
              },
              {
                icon: <Hammer size={32} />,
                title: "Build in public",
                body: "Every dollar is logged. Every win, every loss, every embarrassing demo. Public accountability is the cheat code.",
                color: "bg-marker-red text-cream",
              },
              {
                icon: <Trophy size={32} />,
                title: "Engagement, not ego",
                body: "Our leaderboard ranks you by how much you help — likes, comments, comebacks. Lift the village, get lifted back.",
                color: "bg-marker-blue text-cream",
              },
            ].map((b, i) => (
              <div key={i} className="brutal-card p-6 text-ink relative" style={{ background: "#fff" }}>
                <div className={`inline-flex p-3 border-2 border-ink ${b.color} mb-4 shadow-brutal-sm`}>{b.icon}</div>
                <h3 className="font-display font-extrabold text-2xl uppercase tracking-tight mb-2">{b.title}</h3>
                <p className="leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORUM PREVIEW */}
      <ForumPreview />

      {/* MANIFESTO */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-20">
        <div className="font-hand text-marker-red text-3xl rotate-[-1.5deg] mb-2">— the manifesto —</div>
        <h2 className="font-display font-extrabold uppercase text-4xl sm:text-6xl tracking-tight leading-[0.95]">
          Dreams are funded by{" "}
          <span className="highlight-marker">grit</span>, not{" "}
          <span className="text-marker-red">privilege</span>.
        </h2>
        <div className="mt-8 grid md:grid-cols-2 gap-8 text-lg leading-relaxed">
          <div className="space-y-4">
            <p>
              We don't have a recruiter on speed dial. We don't have rich uncles.
              We don't have a partner track to fall back on. We have <span className="highlight-marker">a laptop, a deadline, and a crowd of people building louder than us</span>.
            </p>
            <p>
              The #NoJobNoVC challenge is simple: build something real, fund it yourself,
              and put every move on the record. No vibes. <em>Receipts.</em>
            </p>
          </div>
          <div className="space-y-4">
            <p>
              When you join, you get a <span className="font-display font-extrabold uppercase">Player Pass</span> —
              a downloadable, share-on-Twitter, pin-to-your-website piece of swagger that
              says: I'm in.
            </p>
            <p>
              Then you ship. You log. You comment on someone else's hustle. The leaderboard
              promotes whoever is helping the most. <span className="font-display font-extrabold uppercase">No job. No VC.</span> Let's go.
            </p>
          </div>
        </div>
        <div className="mt-12 flex flex-wrap items-center gap-4">
          <Link to="/register" className="brutal-btn brutal-btn-red text-base px-6 py-3" data-testid="cta-join-bottom">
            Get my Player Pass <ArrowRight size={18} />
          </Link>
          <span className="font-hand text-2xl rotate-[-2deg] text-ink/70">↑ takes 30 seconds</span>
        </div>
      </section>
    </main>
  );
}
